import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useI18nStore } from "../../stores/i18nStore";
import { LabResult } from "../../types/fhir-health";
import type { Translation } from "../../types/i18n";
import {
  formatChartDate,
  formatNumber,
  formatTooltipTimestamp,
} from "../../utils/dateLocalization";

/*
 * Lab tests are stored with their English name (see COMMON_LAB_TESTS). These
 * maps translate that name, or the LOINC code, for display only.
 */
const LAB_TEST_KEYS_BY_CODE: Record<string, keyof Translation> = {
  "18262-6": "labTestLdl",
  "2093-3": "labTestTotalCholesterol",
  "2085-9": "labTestHdl",
  "2571-8": "labTestTriglycerides",
  "33747-0": "labTestHba1c",
  "718-7": "labTestHemoglobin",
  "6690-2": "labTestWbc",
  "777-3": "labTestPlatelets",
};

const LAB_TEST_KEYS_BY_NAME: Record<string, keyof Translation> = {
  "LDL Cholesterol": "labTestLdl",
  "Total Cholesterol": "labTestTotalCholesterol",
  "HDL Cholesterol": "labTestHdl",
  Triglycerides: "labTestTriglycerides",
  "Hemoglobin A1c": "labTestHba1c",
  HbA1c: "labTestHba1c",
  Hemoglobin: "labTestHemoglobin",
  "White Blood Cell Count": "labTestWbc",
  "Platelet Count": "labTestPlatelets",
};

/** Translated display name of a lab test. A known stored name is translated;
 * the code is only consulted when no name is stored, so a name supplied by an
 * import is shown as it is. */
export const labTestLabel = (
  t: Translation,
  test: { code?: string; name?: string },
): string => {
  const key = test.name
    ? LAB_TEST_KEYS_BY_NAME[test.name]
    : test.code
      ? LAB_TEST_KEYS_BY_CODE[test.code]
      : undefined;
  return key ? (t[key] as string) : test.name || "";
};

const interpretationLabel = (t: Translation, interpretation: string) => {
  switch (interpretation) {
    case "normal":
      return t.normal;
    case "high":
      return t.raised;
    case "low":
      return t.decreased;
    case "critical":
      return t.critical;
    default:
      return interpretation;
  }
};

interface LabResultsChartProps {
  labResults: LabResult[];
  testFilter?: string | undefined;
}

interface ChartDataPoint {
  date: string;
  value: number;
  unit: string;
  interpretation?: "normal" | "high" | "low" | "critical" | undefined;
  displayDate: string;
}

// Define reference ranges for common lab tests
const getReferenceRange = (
  testName: string,
): { optimal?: number; normal?: number; high?: number } => {
  const ranges: Record<
    string,
    { optimal?: number; normal?: number; high?: number }
  > = {
    "LDL Cholesterol": { optimal: 70, normal: 100, high: 160 },
    "HDL Cholesterol": { optimal: 60, normal: 40 },
    "Total Cholesterol": { optimal: 200, high: 240 },
    Triglycerides: { normal: 150, high: 200 },
    Glucose: { normal: 100, high: 126 },
    HbA1c: { normal: 5.7, high: 6.5 },
  };
  return ranges[testName] || {};
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartDataPoint }>;
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
  const { language, t } = useI18nStore();

  if (active && payload && payload.length) {
    const data = payload[0]?.payload as ChartDataPoint | undefined;
    if (!data) return null;
    return (
      <div className="bg-white p-3 border rounded shadow-lg">
        <p className="font-medium">
          {formatTooltipTimestamp(data.date, language)}
        </p>
        <p className="text-blue-600">
          {t.value}: {formatNumber(data.value, language, 2)} {data.unit}
        </p>
        {data.interpretation && (
          <p className="text-sm text-gray-600">
            {t.evaluation}: {interpretationLabel(t, data.interpretation)}
          </p>
        )}
      </div>
    );
  }
  return null;
};

export const LabResultsChart: React.FC<LabResultsChartProps> = ({
  labResults,
  testFilter,
}) => {
  const { language, t } = useI18nStore();

  // Filter and prepare chart data with localized formatting
  const chartData: ChartDataPoint[] = labResults
    .filter((result) => !testFilter || result.name === testFilter)
    .filter((result) => result.valueQuantity?.value !== undefined)
    .map((result) => ({
      date: result.effectiveDateTime || "",
      value: result.valueQuantity!.value!,
      unit: result.valueQuantity?.unit || "",
      interpretation: result.interpretation,
      displayDate: formatChartDate(result.effectiveDateTime || "", language),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500">
          <div className="text-lg mb-2">📊</div>
          <p>{t.labChartNoData}</p>
          {testFilter && (
            <p className="text-sm">
              {t.labChartFilter.replace(
                "{name}",
                labTestLabel(t, { name: testFilter }),
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  const testName = testFilter || labResults[0]?.name || "";
  const testLabel = testName
    ? labTestLabel(t, { name: testName })
    : t.labChartDefaultName;
  const referenceRange = getReferenceRange(testName);
  const unit = chartData[0]?.unit || "";

  return (
    <div className="w-full h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 8, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="displayDate" tick={{ fontSize: 12 }} />
          <YAxis
            tick={{ fontSize: 12 }}
            label={{ value: unit, angle: -90, position: "insideLeft" }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {/* Reference lines for medical thresholds */}
          {referenceRange.optimal && (
            <ReferenceLine
              y={referenceRange.optimal}
              stroke="#22c55e"
              strokeDasharray="5 5"
              label={t.labChartRefOptimal.replace(
                "{value}",
                formatNumber(referenceRange.optimal, language, 2),
              )}
            />
          )}
          {referenceRange.normal && (
            <ReferenceLine
              y={referenceRange.normal}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              label={t.labChartRefNormal.replace(
                "{value}",
                formatNumber(referenceRange.normal, language, 2),
              )}
            />
          )}
          {referenceRange.high && (
            <ReferenceLine
              y={referenceRange.high}
              stroke="#ef4444"
              strokeDasharray="5 5"
              label={t.labChartRefHigh.replace(
                "{value}",
                formatNumber(referenceRange.high, language, 2),
              )}
            />
          )}

          <Line
            type="monotone"
            dataKey="value"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ fill: "#2563eb", strokeWidth: 2, r: 4 }}
            name={testLabel}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
